/**
 * useBulkCreateCleaningTasks — wraps the bulk-create-cleaning-task
 * Edge Function. Returns the per-room created / failed aggregate so
 * the page can show "X of Y tasks created" with the failed list
 * inline. Maps structured error codes to AppError.userMessage.
 *
 * On success (any created rows) invalidates ['my-tasks'],
 * ['pending-inspections'], ['room-board'] so downstream views
 * refresh.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'
import type { CycleType, ServiceTier }                          from './useCreateCleaningTask'

export interface BulkCreateVars {
  roomIds:                  string[]
  cycleType:                CycleType
  serviceTier:              ServiceTier
  assignedToUserId?:        string | null
  estimatedMinutes?:        number | null
  isPartialUnit?:           boolean
  kitchenExcluded?:         boolean
  partialScopeDescription?: string | null
}

export interface BulkCreatedRow {
  room_id:       string
  task_id:       string
  items_seeded:  number
}

export interface BulkFailedRow {
  room_id: string
  code:    string
  detail?: string
}

export interface BulkCreateResult {
  created: BulkCreatedRow[]
  failed:  BulkFailedRow[]
}

export function useBulkCreateCleaningTasks(): UseMutationResult<BulkCreateResult, AppError, BulkCreateVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<BulkCreateResult, AppError, BulkCreateVars>({
    mutationFn: async (vars): Promise<BulkCreateResult> => {
      const { data, error } = await supabase.functions.invoke<BulkCreateResult>('bulk-create-cleaning-task', {
        body: {
          room_ids:                    vars.roomIds,
          cycle_type:                  vars.cycleType,
          service_tier:                vars.serviceTier,
          assigned_to_user_id:         vars.assignedToUserId ?? null,
          estimated_minutes:           vars.estimatedMinutes ?? null,
          is_partial_unit:             vars.isPartialUnit ?? false,
          kitchen_excluded:            vars.kitchenExcluded ?? false,
          partial_scope_description:   vars.partialScopeDescription ?? null,
        },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'BULK_CREATE_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'The server returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: (data) => {
      if (data.created.length > 0) {
        void queryClient.invalidateQueries({ queryKey: ['my-tasks']             })
        void queryClient.invalidateQueries({ queryKey: ['pending-inspections']  })
        void queryClient.invalidateQueries({ queryKey: ['room-board']           })
      }
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'MISSING_FIELD':           return 'Pick at least one room.'
    case 'TOO_MANY_ROOMS':          return 'Max 50 rooms per batch. Split into smaller batches and retry.'
    case 'INVALID_ROOM_ID':         return 'One of the selected room IDs is not valid.'
    case 'INVALID_CYCLE_TYPE':      return 'Pick a valid cycle type.'
    case 'INVALID_SERVICE_TIER':    return 'Pick a valid service tier.'
    case 'INVALID_ASSIGNEE':        return 'The assignee selection is not valid.'
    case 'INVALID_ESTIMATE':        return 'Estimated minutes must be a positive whole number.'
    case 'PARTIAL_SCOPE_TOO_LONG':  return 'Scope description must be 500 characters or fewer.'
    case 'RATE_LIMITED':            return 'Too many bulk batches in a row. Pause for a moment.'
    default:                        return detail || 'Could not create the tasks.'
  }
}

// Per-row error messaging for the failed[] list on the page.
export function perRowFailedMessage(failed: BulkFailedRow): string {
  switch (failed.code) {
    case 'ROOM_HAS_OPEN_TASK': return 'Already had an active task — finish or reject it first.'
    case 'ROOM_NOT_FOUND':     return 'Room no longer exists.'
    case 'ASSIGNEE_NOT_FOUND': return 'Assigned user not found in your org.'
    case 'FORBIDDEN_ORG':      return 'Room is in a different organization.'
    case 'FORBIDDEN_ASSIGNEE': return 'Assignee is not in this room’s organization.'
    case 'INVALID_PARTIAL_FLAGS': return 'Partial-unit details require the partial-unit checkbox.'
    case 'PARTIAL_SCOPE_TOO_LONG': return 'Scope description exceeded the 500-char limit.'
    default:                       return failed.detail || failed.code
  }
}
